import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import MDBox from "components/MDBox";
import CTLNotification from "application/components/CTLNotification";
import CTLTable from "application/components/CTLTable";
import { application } from "globals/endpoints";
import { useMaterialUIController, setActivePage } from "context";

import { Attributes, operationURLS, getColumns } from "./model";

const pad = (n) => String(n).padStart(2, "0");

function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:01`;
}

function defaultTo() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`;
}

function todayAt(hhmm) {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hhmm}`;
}

function buildFilters(from, to) {
  if (!from && !to) return [];
  return [
    { direction: "from", dataType: "date", fieldName: "recordCreatedOn", value: from || "" },
    { direction: "to",   dataType: "date", fieldName: "recordCreatedOn", value: to   || "" },
  ];
}

function CLICommandLog({ onLoad, pageId, searchText, registerCallBacks, pageName }) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, darkMode } = controller;

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo]     = useState(defaultTo);
  const [advancedFilters, setAdvancedFilters] = useState(() => buildFilters(defaultFrom(), defaultTo()));

  const [columns] = useState(getColumns);
  const [loading, setLoading] = useState(false);
  const [capWarning, setCapWarning] = useState(false);

  const [notify, setNotify] = useState({
    isOpen: false, message: "", type: "", pagename: "", status: "",
  });

  useEffect(() => {
    onLoad(Attributes);
    registerCallBacks && registerCallBacks({ ExitPage: handleExit });
  }, [activePage]);

  // Sync parent date-filter state when CTLTable footer "Remove Filter" fires
  useEffect(() => {
    const onClear = () => {
      setFrom("");
      setTo("");
      setCapWarning(false);
      setAdvancedFilters([]);
    };
    window.addEventListener("clearAdvancedFilters", onClear);
    return () => window.removeEventListener("clearAdvancedFilters", onClear);
  }, []);

  function handleFetch() {
    setCapWarning(false);
    setAdvancedFilters(buildFilters(from, to));
  }

  function handleClear() {
    setFrom("");
    setTo("");
    setCapWarning(false);
    setAdvancedFilters([]);
  }

  function handleExit() {
    activePage.key = "rawCdrReport";
    activePage.name = "Raw CDR Report";
    activePage.url = "home/app/rawCdrReport";
    setActivePage(dispatch, activePage);
  }

  function handleExport() {
    setLoading(true);
    setCapWarning(false);
    const columnNames = columns.filter((c) => c.displayInList).map((c) => c.accessor);
    application
      .post(operationURLS.export, { advancedFilters, columnNames }, { responseType: "arraybuffer" })
      .then((response) => {
        const capped = String(response?.headers?.["x-row-cap"] || "") === "true";
        const blob = new Blob([response.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "cli-command-log.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        if (capped) setCapWarning(true);
        setNotify({ isOpen: true, pagename: pageName, type: "success", status: "CSV download successful" });
      })
      .catch(() =>
        setNotify({ isOpen: true, pagename: pageName, type: "error", status: "Unable to download CSV" })
      )
      .finally(() => setLoading(false));
  }

  const dateInputProps = { style: { colorScheme: darkMode ? "dark" : "light" } };

  return (
    <MDBox p={2}>
      {/* Filter bar */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Typography variant="subtitle2" sx={{ flexShrink: 0, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.85 }}>
            CLI Command Log
          </Typography>
          <TextField
            label="From"
            type="datetime-local"
            value={from}
            onFocus={() => { if (!from) setFrom(todayAt("00:01")); }}
            onChange={(e) => {
              const v = e.target.value;
              setFrom(v ? v.substring(0, 11) + "00:01" : "");
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={dateInputProps}
            sx={{ width: 220 }}
          />
          <TextField
            label="To"
            type="datetime-local"
            value={to}
            onFocus={() => { if (!to) setTo(todayAt("23:59")); }}
            onChange={(e) => {
              const v = e.target.value;
              setTo(v ? v.substring(0, 11) + "23:59" : "");
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={dateInputProps}
            sx={{ width: 220 }}
          />
          <Button variant="contained" size="small" onClick={handleFetch} sx={{ flexShrink: 0 }}>
            Fetch
          </Button>
          <Button variant="outlined" size="small" onClick={handleClear} sx={{ flexShrink: 0 }}>
            Clear
          </Button>
          <Button variant="outlined" size="small" onClick={handleExport} disabled={loading} sx={{ flexShrink: 0 }}>
            Export CSV
          </Button>
        </Stack>
        {capWarning && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
            Export was capped at the row limit. Narrow the date range to get a complete extract.
          </Typography>
        )}
      </Paper>

      {/* Loading spinner for export */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Data table — disablePagination: grid receives the full row-capped result set for
          the active window in one response, enabling client-side search (see service note
          on list()) over CLI Command, Execution Started, Is Error, Error Message, Executed. */}
      <CTLTable
        apiURL={operationURLS.list}
        columns={columns}
        advancedFilters={advancedFilters}
        defaultSortColumn="id"
        defaultSortDirection="desc"
        pageId={pageId}
        searchText={searchText}
        hasRowSelection={false}
        disablePagination
      />

      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}

export default CLICommandLog;
